import { consignmentFeePolicyIsValid, evaluateConsignmentUnsoldFees } from './consignment-fees';
import type { ConsignmentAgreement } from './consignment';
import type { ConsignmentStatementClaim } from './consignment-statements';

/** A reviewed ended-listing charge, never an invented sale or automatic collection. */
export interface ConsignmentFeeCharge {
  feeChargeId: string;
  consignorId: string;
  cardId: string;
  intakeId: string;
  agreementId: string;
  termsRevision: number;
  terms: ConsignmentAgreement;
  reason: 'UNSOLD';
  listingCycleId: string;
  externalListingId: string;
  listingStartedAt: string;
  endedAt: string;
  itemKind: 'SINGLE_CARD' | 'SEALED_WAX';
  serviceLevel: 'STANDARD' | 'EXPRESS' | 'PREMIUM';
  components: { unsoldFeeMinor: number; listingFeeMinor: number; serviceFeeMinor: number };
  amountMinor: number;
  currency: 'USD';
  evidenceReference: string;
  status: 'BOOKED' | 'REVERSED';
  version: number;
  createdAt: string;
  createdBy: string;
  reversedAt?: string;
  reversedBy?: string;
  reversalReason?: string;
  statement?: ConsignmentStatementClaim;
}
export type ConsignmentFeeChargeLine = Omit<ConsignmentFeeCharge, 'statement'>;
export interface UnsoldFeeChargeSelection {
  consignorId: string;
  cardId: string;
  /** Persisted marketplace listing key; its provider ID and start date define the cycle. */
  listingCycleId: string;
  itemKind: ConsignmentFeeCharge['itemKind'];
  serviceLevel: ConsignmentFeeCharge['serviceLevel'];
  evidenceReference: string;
  endedAt: string;
}
export interface ConsignmentFeeChargePreview {
  fingerprint: string;
  charge: Omit<ConsignmentFeeChargeLine, 'feeChargeId' | 'createdAt' | 'createdBy'>;
}
export interface ConsignmentFeeChargeEvent {
  action: 'BOOKED' | 'REVERSED';
  at: string;
  actor: string;
  version: number;
  requestId: string;
  reason?: string;
}
export interface ConsignmentFeeChargeDetail {
  charge: ConsignmentFeeCharge;
  events: ConsignmentFeeChargeEvent[];
}
export type ConsignmentFeeChargeMutation =
  | ({ operation: 'PREVIEW_UNSOLD_FEE_CHARGE' } & UnsoldFeeChargeSelection)
  | ({
      operation: 'BOOK_UNSOLD_FEE_CHARGE';
      requestId: string;
      fingerprint: string;
      evidenceReviewed: boolean;
    } & UnsoldFeeChargeSelection)
  | {
      operation: 'REVERSE_FEE_CHARGE';
      consignorId: string;
      feeChargeId: string;
      requestId: string;
      version: number;
      reason: string;
      reviewed: boolean;
    };

export function feeChargeAmount(charge: ConsignmentFeeChargeLine): number {
  return charge.status === 'REVERSED' ? 0 : charge.amountMinor;
}
export function consignmentFeeChargeEvidenceBlock(
  charge: ConsignmentFeeChargeLine,
): string | undefined {
  if (
    charge.reason !== 'UNSOLD' ||
    charge.currency !== 'USD' ||
    !Number.isSafeInteger(charge.amountMinor) ||
    charge.amountMinor <= 0 ||
    !Number.isSafeInteger(charge.version) ||
    charge.version < 1 ||
    !['BOOKED', 'REVERSED'].includes(charge.status)
  )
    return 'Invalid charge evidence';
  if (
    !consignmentFeePolicyIsValid(charge.terms?.feePolicy) ||
    charge.agreementId !== charge.terms.agreementId ||
    charge.consignorId !== charge.terms.consignorId ||
    charge.itemKind !== charge.terms.feePolicy.selectedItemType ||
    charge.serviceLevel !== charge.terms.feePolicy.selectedService
  )
    return 'Invalid frozen charge terms';
  const expected = evaluateConsignmentUnsoldFees(charge.terms.feePolicy);
  if (
    charge.amountMinor !== expected.amountMinor ||
    !charge.components ||
    charge.components.unsoldFeeMinor !== expected.unsoldFeeMinor ||
    charge.components.listingFeeMinor !== expected.listingFeeMinor ||
    charge.components.serviceFeeMinor !== expected.serviceFeeMinor
  )
    return 'Charge does not match frozen fee policy';
  if (
    charge.status === 'REVERSED' &&
    (charge.version !== 2 ||
      !charge.reversedAt ||
      !Number.isFinite(Date.parse(charge.reversedAt)) ||
      !charge.reversedBy ||
      !charge.reversalReason)
  )
    return 'Invalid charge reversal evidence';
  if (
    charge.status === 'BOOKED' &&
    (charge.version !== 1 || charge.reversedAt || charge.reversedBy || charge.reversalReason)
  )
    return 'Invalid booked charge evidence';
  return undefined;
}
export function consignmentFeeChargeBlock(
  charge: ConsignmentFeeCharge,
  ownDraftId?: string,
): string | undefined {
  if (charge.status !== 'BOOKED') return 'Charge has been reversed';
  if (
    charge.statement &&
    !(charge.statement.statementId === ownDraftId && charge.statement.status === 'DRAFT')
  )
    return 'Charge is reserved in another statement';
  return consignmentFeeChargeEvidenceBlock(charge);
}
